import type { FailCodeSectionEntity, FailCodeSubsectionEntity } from '../entities';

import { StsBackendOperation } from '../../sts-backend-operation';

/**
 * Parameters for resolving section and subsection for a fail code on a specific vehicle.
 */
export type ResolveSectionAndSubsectionParams = {
    /** The fail code to resolve sections for. */
    failCode: string;

    /** The vehicle model code (e.g. "GUE"). */
    modelCode?: string;

    /** The vehicle model year (e.g. "2024"). */
    modelYear?: string;

    /** The vehicle option/trim code (e.g. "01"). */
    optionCode?: string;

    /** An optional abort signal for cancelling the request. */
    signal?: AbortSignal;
};

/**
 * Result of the composite section-and-subsection auto-resolution operation.
 *
 * When a user selects a fail code (via search or part lookup), the system fetches possible
 * sections. If exactly one section exists, it is auto-selected and subsections are fetched.
 * If exactly one subsection exists, it is also auto-selected. This type captures the full
 * result of that cascade.
 */
export type ResolveSectionAndSubsectionResult = {
    /**
     * The auto-selected section, if exactly one section was returned.
     * `undefined` when multiple sections exist and the user must choose.
     */
    autoSelectedSection?: FailCodeSectionEntity;

    /**
     * The auto-selected subsection, if exactly one subsection was returned after
     * the section was auto-selected. `undefined` when auto-selection did not occur
     * or when multiple subsections exist.
     */
    autoSelectedSubsection?: FailCodeSubsectionEntity;

    /** All sections returned by the API for the given vehicle and fail code. */
    sectionData: FailCodeSectionEntity[];

    /**
     * Subsections returned by the API for the auto-selected section.
     * Only populated when `autoSelectedSection` is set (i.e., when exactly one section
     * was returned and subsections were automatically fetched).
     */
    subsectionData?: FailCodeSubsectionEntity[];
};

/**
 * Resolve the section and subsection for a given fail code and vehicle, auto-selecting
 * when exactly one option exists at each level.
 *
 * This composite operation encodes a domain rule used by both the search-by-fail-code and
 * search-by-part flows: when a user selects a fail code, fetch the possible sections. If
 * exactly one section is returned, auto-select it and fetch subsections. If exactly one
 * subsection is returned, auto-select it as well. This eliminates unnecessary user clicks
 * when the choice is unambiguous.
 */
export class ResolveSectionAndSubsection extends StsBackendOperation {
    /**
     * Fetch possible sections for the vehicle and fail code, auto-selecting section and
     * subsection when exactly one option exists at each level.
     *
     * @param params - the fail code, vehicle params, and request options
     * @returns the sections, optional auto-selections, and optional subsections
     */
    async exec(params: ResolveSectionAndSubsectionParams): Promise<ResolveSectionAndSubsectionResult> {
        const { failCode, modelCode, modelYear, optionCode, signal } = params;

        // Fetch possible sections for the vehicle and fail code
        const sectionsResponse = await this.client.fetchPossibleSections(
            { failCode, modelCode, modelYear, optionCode },
            signal
        );

        if (!sectionsResponse.success) {
            return { sectionData: [] };
        }

        const sectionData = sectionsResponse.data.sections ?? [];

        // If there is not exactly one section, return early and let the user choose
        if (sectionData.length !== 1) {
            return { sectionData };
        }

        const autoSelectedSection = sectionData[0];

        // Fetch subsections for the auto-selected section
        const subsectionsResponse = await this.client.fetchPossibleSubsections(
            {
                failCode,
                modelCode,
                modelYear,
                optionCode,
                sectionName: autoSelectedSection.sectionName,
                sectionNumber: autoSelectedSection.sectionNumber,
            },
            signal
        );

        if (!subsectionsResponse.success) {
            return { autoSelectedSection, sectionData };
        }

        const subsectionData = subsectionsResponse.data.subsections ?? [];

        // Auto-select the subsection if exactly one was returned
        const autoSelectedSubsection = subsectionData.length === 1 ? subsectionData[0] : undefined;

        return {
            autoSelectedSection,
            autoSelectedSubsection,
            sectionData,
            subsectionData,
        };
    }
}
